<template>
  <div>
    <div class="row mt-0">
      <div class="col-12 col-lg-4 mb-3">
        <div class="card account-profile-card">
          <div class="card-body p-3">
            <div class="account-profile-avatar-row">
              <div
                :class="[
                  'account-avatar',
                  `account-avatar--${avatarColorIndex(account.userName)}`,
                ]"
              >
                {{ initials(account.userName) }}
              </div>
              <div class="flex-grow-1" style="min-width: 0">
                <h6 class="mb-0 d-flex align-items-center gap-2">
                  <span class="text-truncate">{{ account.userName }}</span>
                  <span
                    class="account-status-dot"
                    :class="account.active == 1 ? 'is-active' : 'is-active'"
                  ></span>
                </h6>
                <p class="text-xs text-secondary mb-0 text-truncate">
                  {{ account.email }}
                </p>
              </div>
            </div>

            <div class="d-flex justify-content-between align-items-center mb-2">
              <span class="text-xs text-secondary">Primary Role</span>
              <span class="account-pill-badge">{{ primaryRole }}</span>
            </div>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <span class="text-xs text-secondary">Member since</span>
              <span class="text-xs fw-bold">{{ memberSince }}</span>
            </div>

            <ul
              class="nav nav-pills nav-fill account-config-tabs bg-transparent p-1 mb-3"
            >
              <li class="nav-item">
                <a
                  class="nav-link active"
                  data-bs-toggle="tab"
                  href="#uftab"
                  @click="activeTab = 'fields'"
                >
                  User Fields
                </a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  data-bs-toggle="tab"
                  href="#roletab"
                  @click="activeTab = 'roles'"
                >
                  Roles
                </a>
              </li>
              <li class="nav-item">
                <a
                  class="nav-link"
                  data-bs-toggle="tab"
                  href="#permissionstab"
                  @click="activeTab = 'permissions'"
                >
                  Special Permissions
                </a>
              </li>
            </ul>

            <div class="input-group input-group-sm mb-4">
                <span class="input-group-text">
                  <i class="fas fa-search"></i>
                </span>
                <input
                  v-if="activeTab === 'fields'"
                  v-model="fieldSearch"
                  class="form-control"
                  placeholder="Search fields..."
                />
                <input
                  v-else-if="activeTab === 'roles'"
                  v-model="roleSearch"
                  class="form-control"
                  placeholder="Search roles..."
                />
                <input
                  v-else
                  :value="permissionsFilter"
                  class="form-control"
                  placeholder="Quick search by name or app..."
                  @input="$emit('update-permissions-filter', $event.target.value)"
                />
              </div>

            <div class="account-profile-tips mb-3">
              <p class="account-profile-tips-title">
                <i class="fas fa-info-circle"></i>
                Configuration Tips
              </p>
              <ul>
                <li>Changes/Filters apply immediately.</li>
              </ul>
            </div>

            <div class="account-profile-actions">
              <button
                type="button"
                class="account-profile-action-btn"
                @click="$emit('view-password', account)"
              >
                <i class="fas fa-eye"></i>
                View password
              </button>
              <button
                type="button"
                class="account-profile-action-btn"
                @click="$emit('reset-password', account)"
              >
                <i class="fas fa-key"></i>
                Reset password
              </button>

              <button
                type="button"
                class="btn btn-outline-secondary"
                @click="$emit('close')"
              >
                <i class="fas fa-arrow-left"></i>
                Back
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 col-lg-8 mb-3">
        <div class="card shadow-lg account-config-detail-card">
          <div class="card-body p-3 d-flex flex-column h-100">
            <div class="d-flex justify-content-between align-items-start mb-3">
              <div>
                <h5 class="mb-0">
                  {{
                    activeTab === "fields"
                      ? "User Fields Configuration"
                      : activeTab === "roles"
                        ? "Roles Configuration"
                        : "Special Permissions"
                  }}
                </h5>
                <p class="text-sm text-secondary mb-0">
                  {{
                    activeTab === "fields"
                      ? "Manage field visibility for this account"
                      : activeTab === "roles"
                        ? "Manage role assignments for this account"
                        : "Manage individual permissions for this account"
                  }}
                </p>
              </div>
              <span class="account-pill-badge">
                {{ activeCount }} ACTIVE
                {{
                  activeTab === "fields"
                    ? "FIELDS"
                    : activeTab === "roles"
                      ? "ROLES"
                      : "PERMISSIONS"
                }}
              </span>
            </div>

            <!--  -->

            <div class="flex-grow-1 overflow-auto">
              <div class="tab-content">
                <div id="uftab" class="tab-pane fade show active">
                  <div v-for="field in filteredUserFields" :key="field.id">
                    <div class="account-config-row">
                      <div class="account-config-icon">
                        <i class="fas fa-tag"></i>
                      </div>
                      <div class="d-flex justify-content-between flex-grow-1">
                        <div>
                          <p class="fw-bold mb-0">{{ field.name }}</p>
                          <p class="text-xs text-secondary mb-0">
                            {{ field.description }}
                          </p>
                        </div>
                        <el-switch
                          v-if="userID != -1"
                          v-model="field.isActive"
                          active-color="#10b981"
                          @change="$emit('update-field-status', field)"
                        />
                      </div>
                    </div>
                    <div
                      v-if="field.isActive && userID != -1"
                      class="mt-2 ms-5"
                    >
                      <input
                        class="form-control"
                        :value="fieldValues[field.id]"
                        @input="$emit('field-input', { event: $event, field })"
                      />
                    </div>
                  </div>
                </div>

                <div id="roletab" class="tab-pane fade">
                  <div v-for="role in filteredRoles" :key="role.id">
                    <div class="account-config-row">
                      <div class="account-config-icon">
                        <i class="fas fa-shield-alt"></i>
                      </div>
                      <div class="d-flex justify-content-between flex-grow-1">
                        <div>
                          <p class="fw-bold mb-0">{{ role.name }}</p>
                          <p class="text-xs text-secondary mb-0">
                            {{ role.description }}
                          </p>
                        </div>
                        <el-switch
                          v-if="userID != -1"
                          v-model="role.isActive"
                          active-color="#10b981"
                          @change="$emit('update-role-status', role)"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div id="permissionstab" class="tab-pane fade">
                  <div v-if="permissionsLoading" class="account-loading-state">
                    <i class="fas fa-circle-notch fa-spin"></i>
                    <p class="mb-0">Loading permissions...</p>
                  </div>
                  <template v-else>
                    <div v-for="permission in permissions" :key="permission.id">
                      <div class="account-config-row">
                        <div class="account-config-icon">
                          <i class="fas fa-key"></i>
                        </div>
                        <div class="d-flex justify-content-between flex-grow-1">
                          <div>
                            <p class="fw-bold mb-0">{{ permission.name }}</p>
                            <p class="text-xs text-secondary mb-0">
                              <span
                                class="permission-method-badge"
                                :class="`method-${(permission.method || '').toLowerCase()}`"
                              >
                                {{ permission.method }}
                              </span>
                              &middot; {{ permission.controller }}
                            </p>
                            <span
                              v-if="isPermissionInherited(permission.id)"
                              class="permission-inherited-badge"
                            >
                              Inherited from role
                            </span>
                          </div>
                          <el-switch
                            v-if="userID != -1"
                            :model-value="isPermissionAssigned(permission.id)"
                            :disabled="isPermissionInherited(permission.id)"
                            active-color="#10b981"
                            @change="
                              (value) =>
                                $emit('toggle-permission', {
                                  id: permission.id,
                                  enabled: value,
                                })
                            "
                          />
                        </div>
                      </div>
                    </div>
                  </template>
                  <div class="d-flex justify-content-center mt-2">
                    <el-pagination
                      background
                      layout="prev, pager, next"
                      :current-page="permissionsPageNumber"
                      :page-size="permissionsPageSize"
                      :total="permissionsTotalRecords"
                      @current-change="
                        (value) => $emit('update-permissions-page', value)
                      "
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed } from "vue";

const AVATAR_COLOR_COUNT = 8;

const avatarColorIndex = (username) => {
  if (!username) return 0;
  let hash = 0;
  for (let i = 0; i < username.length; i++) {
    hash = (hash * 31 + username.charCodeAt(i)) % AVATAR_COLOR_COUNT;
  }
  return hash;
};

const initials = (username) => {
  if (!username) return "?";
  return username.slice(0, 2).toUpperCase();
};

export default defineComponent({
  name: "account-config-panel",
  props: {
    account: {
      type: Object,
      required: true,
    },
    roles: {
      type: Array,
      required: true,
    },
    userFields: {
      type: Array,
      required: true,
    },
    userID: {
      type: Number,
      required: true,
    },
    fieldValues: {
      type: Object,
      required: true,
    },
    permissions: {
      type: Array,
      required: true,
    },
    permissionsTotalRecords: {
      type: Number,
      required: true,
    },
    permissionsPageSize: {
      type: Number,
      required: true,
    },
    permissionsPageNumber: {
      type: Number,
      required: true,
    },
    permissionsFilter: {
      type: String,
      required: true,
    },
    assignedPermissionIds: {
      type: Array,
      required: true,
    },
    permissionsLoading: {
      type: Boolean,
      required: true,
    },
  },
  emits: [
    "close",
    "update-field-status",
    "update-role-status",
    "field-input",
    "view-password",
    "reset-password",
    "toggle-permission",
    "update-permissions-filter",
    "update-permissions-page-size",
    "update-permissions-page",
  ],
  setup(props) {
    const activeTab = ref("fields");
    const fieldSearch = ref("");
    const roleSearch = ref("");
    const permissionPageSizes = ref([10, 20, 50, 100]);

    const primaryRole = computed(() => {
      const active = props.roles.find((r) => r.isActive);
      return active ? active.name : "No role assigned";
    });

    const memberSince = computed(() => {
      if (!props.account?.createDate) return "—";
      const parsed = new Date(props.account.createDate);
      return Number.isNaN(parsed.getTime()) ? "—" : parsed.toLocaleDateString();
    });

    const inheritedPermissionIds = computed(() => {
      const ids = new Set();
      for (const role of props.roles) {
        if (!role.isActive) continue;
        for (const permission of role.permissions || []) {
          ids.add(permission.id);
        }
      }
      return ids;
    });

    const isPermissionInherited = (id) => inheritedPermissionIds.value.has(id);

    const isPermissionAssigned = (id) =>
      isPermissionInherited(id) || props.assignedPermissionIds.includes(id);

    const activeCount = computed(() => {
      if (activeTab.value === "fields") {
        return props.userFields.filter((item) => item.isActive).length;
      }
      if (activeTab.value === "roles") {
        return props.roles.filter((item) => item.isActive).length;
      }
      const ids = new Set(props.assignedPermissionIds);
      inheritedPermissionIds.value.forEach((id) => ids.add(id));
      return ids.size;
    });

    const filteredUserFields = computed(() => {
      const term = fieldSearch.value.trim().toLowerCase();
      if (!term) return props.userFields;
      return props.userFields.filter(
        (f) =>
          (f.name || "").toLowerCase().includes(term) ||
          (f.description || "").toLowerCase().includes(term),
      );
    });

    const filteredRoles = computed(() => {
      const term = roleSearch.value.trim().toLowerCase();
      if (!term) return props.roles;
      return props.roles.filter(
        (r) =>
          (r.name || "").toLowerCase().includes(term) ||
          (r.description || "").toLowerCase().includes(term),
      );
    });

    return {
      activeTab,
      fieldSearch,
      roleSearch,
      permissionPageSizes,
      primaryRole,
      memberSince,
      activeCount,
      filteredUserFields,
      filteredRoles,
      isPermissionInherited,
      isPermissionAssigned,
      initials,
      avatarColorIndex,
    };
  },
});
</script>
